// Your own faces for Nick and Raf.
//
// Kept in IndexedDB on this device and nowhere else: nothing is uploaded,
// nothing is sent anywhere, and there is no account for it to be attached to.
// That is the whole reason the feature is safe to offer — a photo of someone
// you know belongs on your phone, not in a store listing.

import { ref } from 'vue';
import { records } from '../core/storage.js';

/** Longest edge, in pixels. Big enough for a 21px sprite at any density. */
export const MAX_EDGE = 256;

/** Refuse early and say why, rather than storing something that will not draw. */
export const ACCEPTED = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
export const MAX_INPUT_BYTES = 20 * 1024 * 1024;

export const WHO = ['nick', 'raf'];
export const recordId = (who) => `face:${who}`;

/** Data URLs, or null for "use the drawn character". */
export const faces = ref({ nick: null, raf: null });

/**
 * What is wrong with this file, or null if nothing is.
 *
 * Separated from the DOM work so the rules are testable without a browser —
 * the part that actually decodes an image cannot be.
 */
export function reject(file) {
  if (!file) return 'No file chosen.';
  if (file.size > MAX_INPUT_BYTES) {
    return `That image is ${Math.round(file.size / 1024 / 1024)}MB. The limit is ${MAX_INPUT_BYTES / 1024 / 1024}MB.`;
  }
  const type = String(file.type || '').toLowerCase();
  if (!type.startsWith('image/')) return 'That file is not an image.';
  if (!ACCEPTED.includes(type)) return 'Use a PNG, JPEG, WebP or GIF.';
  return null;
}

/** Fit within a square of `max`, never scaling a small image up. */
export function fit(width, height, max = MAX_EDGE) {
  const longest = Math.max(width, height);
  if (!longest) return { width: 0, height: 0 };
  const scale = Math.min(1, max / longest);
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

/**
 * Where a crop sits: how far the picture is zoomed, and how far it is dragged,
 * expressed so the same numbers work at any preview size.
 *
 * `zoom` is a multiple of "just covers the square". `x`/`y` are offsets in
 * preview pixels from centred. Kept as plain data so the maths below can be
 * tested without a DOM.
 */
export const DEFAULT_CROP = { zoom: 1, x: 0, y: 0 };
export const ZOOM_MIN = 1;
export const ZOOM_MAX = 4;

/** The scale at which the picture exactly covers a square of `size`. */
export function coverScale(width, height, size) {
  if (!width || !height) return 1;
  return Math.max(size / width, size / height);
}

/**
 * Clamp a drag so the square never shows past the edge of the picture.
 *
 * Without this you can drag a face until half the frame is background, and on a
 * board the size of a thumbnail that just looks like the app lost the image.
 */
export function clampOffset(crop, width, height, size) {
  const scale = coverScale(width, height, size) * crop.zoom;
  const slackX = Math.max(0, (width * scale - size) / 2);
  const slackY = Math.max(0, (height * scale - size) / 2);
  return {
    zoom: Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, crop.zoom)),
    x: Math.max(-slackX, Math.min(slackX, crop.x)),
    y: Math.max(-slackY, Math.min(slackY, crop.y)),
  };
}

/** Render the chosen square of a loaded image at `out` pixels. */
export function cropToSquare(img, crop, previewSize, out = MAX_EDGE) {
  const canvas = document.createElement('canvas');
  canvas.width = out;
  canvas.height = out;
  const ctx = canvas.getContext('2d');
  const k = out / previewSize;
  const scale = coverScale(img.naturalWidth, img.naturalHeight, previewSize) * crop.zoom * k;
  const w = img.naturalWidth * scale;
  const h = img.naturalHeight * scale;
  ctx.drawImage(img, (out - w) / 2 + crop.x * k, (out - h) / 2 + crop.y * k, w, h);
  return canvas.toDataURL('image/png');
}

/** Load a File into an Image, and hand back the object URL to revoke. */
export function loadImage(file) {
  return new Promise((resolve, reject_) => {
    const why = reject(file);
    if (why) { reject_(new Error(why)); return; }
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => resolve({ img, revoke: () => URL.revokeObjectURL(url) });
    img.onerror = () => { URL.revokeObjectURL(url); reject_(new Error('That image could not be read.')); };
    img.src = url;
  });
}

/** Store an already-cropped data URL. */
export async function saveFace(who, dataUrl) {
  await records.put({ id: recordId(who), data: dataUrl });
  faces.value = { ...faces.value, [who]: dataUrl };
  return dataUrl;
}

/** Decode, shrink, and re-encode as PNG — PNG because cut-outs have alpha. */
export function downscale(file, max = MAX_EDGE) {
  return new Promise((resolve, reject_) => {
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const { width, height } = fit(img.naturalWidth, img.naturalHeight, max);
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      try { resolve(canvas.toDataURL('image/png')); }
      catch { reject_(new Error('That image could not be read.')); }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject_(new Error('That image could not be read.'));
    };
    img.src = url;
  });
}

export async function loadFaces() {
  const next = { nick: null, raf: null };
  await Promise.all(WHO.map(async (who) => {
    try {
      const row = await records.get(recordId(who));
      if (row?.data) next[who] = row.data;
    } catch { /* private mode, or no database yet */ }
  }));
  faces.value = next;
  return next;
}

export async function setFace(who, file) {
  const why = reject(file);
  if (why) throw new Error(why);
  const data = await downscale(file);
  await records.put({ id: recordId(who), data });
  faces.value = { ...faces.value, [who]: data };
  return data;
}

export async function clearFace(who) {
  await records.delete(recordId(who)).catch(() => {});
  faces.value = { ...faces.value, [who]: null };
}
